/**
 * Texts of screen E8 · Positions (specs/M5-positions/spec.md §4; mockup 7,
 * docs/04-Maquettes-FXMate/00-Maquettes-FXMate-transcription.md §7, French original in commit 971cc96).
 *
 * Provisional until Q3 (tone; French uses "tu", as in the mockups) and Q15 (EN mockups).
 * Reason texts (POSITION_TRACKING_LOST, STOP_LOSS_REFUSED_BY_BROKER, …) live in the C2 catalogue.
 *
 * `understood.*` are the "Understood: …" templates (spec R7): the screen builds the sentence from
 * the structured interpretation, the API never sends interpretation text (I12, master X2, X5).
 * The channel's own message is never translated (spec R6).
 */
export const positionsFr = {
  title: 'Positions',
  open: 'Ouvertes · {{count}}',
  /** The two tabs of mockup 7 and Historique (M6): « Ouvertes » until the count is known. */
  tabs: { label: 'Vues des positions', open: 'Ouvertes', history: 'Historique' },
  loading: 'Chargement des positions…',
  retry: 'Réessayer',
  empty: 'Aucune position ouverte par FXMate.',
  manual_one: '{{count}} autre position ouverte par toi dans MetaTrader : FXMate ne la gère pas.',
  manual_other:
    '{{count}} autres positions ouvertes par toi dans MetaTrader : FXMate ne les gère pas.',
  side: { BUY: 'BUY', SELL: 'SELL' },
  status: {
    OPENING: 'Ouverture en cours',
    PARTIALLY_CLOSED: 'Partiellement fermée',
    CLOSING: 'Clôture en cours',
    TRACKING_LOST: 'Suivi interrompu',
  },
  field: {
    channel: 'Canal',
    size: 'Taille',
    entry: 'Entrée annoncée / obtenue',
    stopLoss: 'Stop loss',
    takeProfit: 'Take profit',
    riskAtStop: 'Risque au SL',
  },
  lots: '{{lots}} lot',
  market: 'marché',
  atBreakEven: '{{price}} · au BE',
  /** A last known result on a lost position (spec §4, Q54). `time` is HH:MM, local. */
  resultAsOf: 'au {{time}}',
  none: '—',
  /** Risk at stop loss (specs/P10-risk-at-stop/spec.md §4). Words, never a number, for no stop. */
  risk: {
    protected: 'protégée',
    noStop: 'pas de stop',
    unavailable: 'Indisponible pour le moment',
  },
  timeline: {
    title: 'Ce que le canal a dit, ce que FXMate a fait',
    loading: 'Chargement des messages du canal…',
    understood: 'Compris :',
    edited: 'Message corrigé par le canal',
    reply: 'en réponse :',
    deleted: 'Le canal a supprimé ce message. La position n’est pas fermée pour autant.',
    executed: 'Exécuté',
    failed: 'Non exécuté :',
    pending: 'En attente',
    notApplied: 'Non appliqué :',
    notAppliedNoReason: 'Non appliqué (motif non précisé)',
    outcomeUnknown: 'État de l’exécution non affichable dans cette version',
  },
  understood: {
    buy: 'achat',
    sell: 'vente',
    openUnknownSide: 'ouverture',
    atMarket: 'au marché',
    stopLoss: 'SL {{price}}',
    takeProfit: 'TP {{price}}',
    stopLossMoved: 'stop loss déplacé à {{price}}',
    takeProfitMoved: 'take profit déplacé à {{price}}',
    breakEven: 'stop loss déplacé au point d’entrée',
    breakEvenAt: 'stop loss déplacé au point d’entrée ({{price}})',
    levelsRemoved: 'suppression du stop loss ou du take profit',
    modified: 'position modifiée',
    partialClose: 'fermeture de {{percent}} % de la position',
    partialCloseUnknown: 'fermeture partielle de la position',
    close: 'fermeture de la position',
    closeAll: 'fermeture de toutes les positions du canal',
    cancel: 'signal annulé : fermeture de la position',
    ambiguous: 'message ambigu, aucune action',
    unknown: 'instruction que cette version ne sait pas afficher',
  },
};

/** Shape of the `positions` section of `Messages` (derived from the French object, like Home). */
export type PositionsMessages = typeof positionsFr;

/** English texts of screen E8. `PositionsMessages` makes a missing or extra key a compile error. */
export const positionsEn: PositionsMessages = {
  title: 'Positions',
  open: 'Open · {{count}}',
  tabs: { label: 'Position views', open: 'Open', history: 'History' },
  loading: 'Loading positions…',
  retry: 'Retry',
  empty: 'No position opened by FXMate.',
  manual_one: '{{count}} other position opened by you in MetaTrader: FXMate does not manage it.',
  manual_other:
    '{{count}} other positions opened by you in MetaTrader: FXMate does not manage them.',
  side: { BUY: 'BUY', SELL: 'SELL' },
  status: {
    OPENING: 'Opening',
    PARTIALLY_CLOSED: 'Partially closed',
    CLOSING: 'Closing',
    TRACKING_LOST: 'Tracking lost',
  },
  field: {
    channel: 'Channel',
    size: 'Size',
    entry: 'Entry announced / filled',
    stopLoss: 'Stop loss',
    takeProfit: 'Take profit',
    riskAtStop: 'Risk at SL',
  },
  lots: '{{lots}} lot',
  market: 'market',
  atBreakEven: '{{price}} · at BE',
  resultAsOf: 'as of {{time}}',
  none: '—',
  risk: {
    protected: 'protected',
    noStop: 'no stop',
    unavailable: 'Not available right now',
  },
  timeline: {
    title: 'What the channel said, what FXMate did',
    loading: 'Loading the channel’s messages…',
    understood: 'Understood:',
    edited: 'Message corrected by the channel',
    reply: 'in reply:',
    deleted: 'The channel deleted this message. The position is not closed because of it.',
    executed: 'Executed',
    failed: 'Not executed:',
    pending: 'Pending',
    notApplied: 'Not applied:',
    notAppliedNoReason: 'Not applied (no reason given)',
    outcomeUnknown: 'Execution state this version cannot show',
  },
  understood: {
    buy: 'buy',
    sell: 'sell',
    openUnknownSide: 'open',
    atMarket: 'at market',
    stopLoss: 'SL {{price}}',
    takeProfit: 'TP {{price}}',
    stopLossMoved: 'stop loss moved to {{price}}',
    takeProfitMoved: 'take profit moved to {{price}}',
    breakEven: 'stop loss moved to break-even',
    breakEvenAt: 'stop loss moved to break-even ({{price}})',
    levelsRemoved: 'stop loss or take profit removed',
    modified: 'position modified',
    partialClose: 'close {{percent}}% of the position',
    partialCloseUnknown: 'partial close of the position',
    close: 'close the position',
    closeAll: 'close all the channel’s positions',
    cancel: 'signal cancelled: close the position',
    ambiguous: 'unclear message, no action',
    unknown: 'an instruction this version cannot show',
  },
};
